import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Fade,
  useMediaQuery,
  useTheme,
  Stack,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { evaluationDatabaseApi } from '../../services/evaluationDatabaseApi';

const EvaluationDatabaseManage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // 數據狀態
  const [environments, setEnvironments] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [stances, setStances] = useState([]);
  const [problemCategories, setProblemCategories] = useState([]);
  const [problems, setProblems] = useState([]);
  const [improvementCategories, setImprovementCategories] = useState([]);
  const [movementDetails, setMovementDetails] = useState([]);
  const [improvements, setImprovements] = useState([]);
  
  // 對話框狀態
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  
  // 分頁狀態
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // 新增：可用動作詳細狀態
  const [availableMovementDetails, setAvailableMovementDetails] = useState([]);
  
  // 新增：問題類別過濾狀態
  const [selectedProblemCategoryType, setSelectedProblemCategoryType] = useState('performance');
  
  // 新增：改善類別過濾狀態
  const [selectedImprovementCategoryType, setSelectedImprovementCategoryType] = useState('exercise');
  
  // 新增：動作詳細技能類別篩選狀態
  const [selectedSkillCategory, setSelectedSkillCategory] = useState('all');
  
  // 新增：改善項目統計篩選狀態
  const [selectedImprovementStatsCategory, setSelectedImprovementStatsCategory] = useState('all');

  const tabConfig = [
    { label: '環境', key: 'environments', data: environments, setData: setEnvironments },
    { label: '裝備', key: 'equipment', data: equipment, setData: setEquipment },
    { label: '姿勢', key: 'stances', data: stances, setData: setStances },
    { label: '問題類別', key: 'problemCategories', data: problemCategories, setData: setProblemCategories },
    { label: '問題', key: 'problems', data: problems, setData: setProblems },
    { label: '改善類別', key: 'improvementCategories', data: improvementCategories, setData: setImprovementCategories },
    { label: '動作詳細', key: 'movementDetails', data: movementDetails, setData: setMovementDetails },
    { label: '改善項目', key: 'improvements', data: improvements, setData: setImprovements },
  ];

  // API 方法映射
  const apiMethodMap = {
    environments: { create: 'createEnvironment', update: 'updateEnvironment', delete: 'deleteEnvironment' },
    equipment: { create: 'createEquipment', update: 'updateEquipment', delete: 'deleteEquipment' },
    stances: { create: 'createStance', update: 'updateStance', delete: 'deleteStance' },
    problemCategories: { create: 'createProblemCategory', update: 'updateProblemCategory', delete: 'deleteProblemCategory' },
    problems: { create: 'createProblem', update: 'updateProblem', delete: 'deleteProblem' },
    improvementCategories: { create: 'createImprovementCategory', update: 'updateImprovementCategory', delete: 'deleteImprovementCategory' },
    movementDetails: { create: 'createMovementDetail', update: 'updateMovementDetail', delete: 'deleteMovementDetail' },
    improvements: { create: 'createImprovement', update: 'updateImprovement', delete: 'deleteImprovement' }
  };

  useEffect(() => {
    loadAllData();
  }, []);

  // 自定義技能類別排序順序
  const skillCategoryOrder = [
    'position',
    'balancing', 
    'rotation',
    'counter_rotation',
    'inclination',
    'angulation',
    'reactive',
    'proactive',
    'timing',
    'coordination'
  ];

  // 自定義改善類別排序函數
  const sortImprovementCategories = (categories) => {
    return categories.sort((a, b) => {
      // 先按 category_type 排序
      if (a.category_type !== b.category_type) {
        return a.category_type.localeCompare(b.category_type);
      }
      
      // 再按 skill_category 自定義順序排序
      const aSkillIndex = a.skill_category ? skillCategoryOrder.indexOf(a.skill_category) : -1;
      const bSkillIndex = b.skill_category ? skillCategoryOrder.indexOf(b.skill_category) : -1;
      
      // 如果 skill_category 不在預定義順序中，放在最後
      if (aSkillIndex === -1 && bSkillIndex === -1) {
        return a.name.localeCompare(b.name);
      }
      if (aSkillIndex === -1) return 1;
      if (bSkillIndex === -1) return -1;
      
      if (aSkillIndex !== bSkillIndex) {
        return aSkillIndex - bSkillIndex;
      }
      
      // 最後按名稱排序
      return a.name.localeCompare(b.name);
    });
  };

  // 自定義動作詳細排序函數
  const sortMovementDetails = (details) => {
    return details.sort((a, b) => {
      // 先按 skill_category 自定義順序排序
      const aSkillIndex = a.skill_category ? skillCategoryOrder.indexOf(a.skill_category) : -1;
      const bSkillIndex = b.skill_category ? skillCategoryOrder.indexOf(b.skill_category) : -1;
      
      // 如果 skill_category 不在預定義順序中，放在最後
      if (aSkillIndex === -1 && bSkillIndex === -1) {
        // 如果都沒有技能分類，按難度排序（難度低的先）
        const aDifficulty = a.difficulty_level || 1;
        const bDifficulty = b.difficulty_level || 1;
        if (aDifficulty !== bDifficulty) {
          return aDifficulty - bDifficulty;
        }
        return a.name.localeCompare(b.name);
      }
      if (aSkillIndex === -1) return 1;
      if (bSkillIndex === -1) return -1;
      
      if (aSkillIndex !== bSkillIndex) {
        return aSkillIndex - bSkillIndex;
      }
      
      // 技能分類相同時，按難度排序（難度低的先）
      const aDifficulty = a.difficulty_level || 1;
      const bDifficulty = b.difficulty_level || 1;
      if (aDifficulty !== bDifficulty) {
        return aDifficulty - bDifficulty;
      }
      
      // 最後按名稱排序
      return a.name.localeCompare(b.name);
    });
  };

  // 自定義改善項目排序函數（與改善項目統計一致）
  const sortImprovements = (improvements) => {
    return [...improvements].sort((a, b) => {
      // 先按 category_type 排序
      if (a.category_type !== b.category_type) {
        return a.category_type.localeCompare(b.category_type);
      }
      
      // 再按 skill_category 自定義順序排序
      const aSkillIndex = a.skill_category ? skillCategoryOrder.indexOf(a.skill_category) : -1;
      const bSkillIndex = b.skill_category ? skillCategoryOrder.indexOf(b.skill_category) : -1;
      
      // 如果 skill_category 不在預定義順序中，放在最後
      if (aSkillIndex === -1 && bSkillIndex === -1) {
        return a.category_name.localeCompare(b.category_name);
      }
      if (aSkillIndex === -1) return 1;
      if (bSkillIndex === -1) return -1;
      
      if (aSkillIndex !== bSkillIndex) {
        return aSkillIndex - bSkillIndex;
      }
      
      // 最後按 category_name 排序
      return a.category_name.localeCompare(b.category_name);
    });
  };

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [
        envData, equipData, stanceData, problemCatData,
        problemData, improvementCatData, movementData, improvementData,
      ] = await Promise.all([
        evaluationDatabaseApi.getEnvironments(),
        evaluationDatabaseApi.getEquipment(),
        evaluationDatabaseApi.getStances(),
        evaluationDatabaseApi.getProblemCategories(),
        evaluationDatabaseApi.getProblems(),
        evaluationDatabaseApi.getImprovementCategories(),
        evaluationDatabaseApi.getMovementDetails(),
        evaluationDatabaseApi.getImprovements(),
      ]);

      setEnvironments(envData);
      setEquipment(equipData);
      setStances(stanceData);
      setProblemCategories(problemCatData);
      setProblems(problemData);
      // 對改善類別應用自定義排序
      setImprovementCategories(sortImprovementCategories(improvementCatData));
      setMovementDetails(sortMovementDetails(movementData));
      // 對改善項目應用自定義排序（與改善項目統計一致）
      setImprovements(sortImprovements(improvementData));
    } catch (err) {
      setError('載入資料失敗: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPage(0);
    
    // 當切換到動作詳細標籤時重置技能類別篩選
    if (newValue === 6) {
      setSelectedSkillCategory('position');
    }
    
    // 當切換到改善項目標籤時重置統計篩選
    if (newValue === 7) {
      setSelectedImprovementStatsCategory('position');
    }
  };

  const handleOpenDialog = async (item = null) => {
    setEditingItem(item);
    let formData = item || getDefaultFormData();
    
    // 如果是編輯改善項目，需要處理 movement_detail_orders
    if (item && tabConfig[activeTab].key === 'improvements') {
      const movementDetailOrders = item.improvement_movement_details 
        ? item.improvement_movement_details.map((detail, index) => ({
            movement_detail_id: detail.movement_detail.id,
            order: detail.order || index
          }))
        : [];
      
      formData = {
        ...item,
        movement_detail_orders: movementDetailOrders
      };
    }
    
    setFormData(formData);
    setOpenDialog(true);
    
    // 如果是改善項目，載入並過濾動作詳細
    if (tabConfig[activeTab].key === 'improvements') {
      try {
        const movementDetails = await evaluationDatabaseApi.getAllMovementDetails();
        
        // 獲取技能類別
        let skillCategory = null;
        if (item) {
          // 編輯模式：從改善類別中獲取技能類別
          const improvementCategory = improvementCategories.find(cat => cat.id === item.category);
          skillCategory = improvementCategory?.skill_category;
        } else {
          // 新增模式：從預設選擇的類別中獲取技能類別
          const improvementCategory = improvementCategories.find(cat => cat.id === formData.category);
          skillCategory = improvementCategory?.skill_category;
        }
        
        if (skillCategory) {
          const filteredDetails = movementDetails.filter(detail => 
            detail.skill_category === skillCategory
          );
          setAvailableMovementDetails(filteredDetails);
        } else {
          setAvailableMovementDetails(movementDetails);
        }
      } catch (err) {
        console.error('Failed to load movement details:', err);
        setAvailableMovementDetails([]);
      }
    } else {
      setAvailableMovementDetails([]);
    }
  };

  // 添加缺失的 handleCloseDialog 函數
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
    setFormData({});
    setError('');
    setAvailableMovementDetails([]);
  };

  // 修改 getDefaultFormData 中的 movementDetails 預設值
  const getDefaultFormData = () => {
    const currentTab = tabConfig[activeTab];
    const defaults = {
      environments: { name: '', description: '' },
      equipment: { name: '', description: '' },
      stances: { name: '', description: '' },
      problemCategories: { name: '', category_type: selectedProblemCategoryType, description: '' },
      problems: { category: '', name: '', description: '', severity_level: 1 },
      improvementCategories: { name: '', category_type: selectedImprovementCategoryType, description: '' },
      movementDetails: { 
        name: '', 
        description: '', 
        difficulty_level: 1, 
        skill_category: 'position',
        move_direction: 'up_and_down'
      },
      improvements: { 
        category: '', 
        name: '', // 新增模式下不預設名稱
        description: '', 
        effectiveness_level: 1, 
        movement_detail_orders: [] // 動作詳細順序
      },
    };
    
    // 如果是改善項目且為新增模式，自動選擇第一個可用的類別
    if (currentTab.key === 'improvements' && !editingItem) {
      const availableCategories = improvementCategories
        .filter(cat => cat.category_type === 'movement')
        .filter(cat => {
          const hasImprovement = improvements.some(imp => imp.category === cat.id);
          return !hasImprovement;
        });
      
      if (availableCategories.length > 0) {
        const firstCategory = availableCategories[0];
        defaults.improvements.category = firstCategory.id;
        defaults.improvements.name = firstCategory.skill_category || '';
      }
    }
    
    return defaults[currentTab.key] || {};
  };

  // 修改 renderFormFields 函數
  const renderFormFields = () => {
    const currentTab = tabConfig[activeTab];
    
    return (
      <Box>
        {/* 改善項目的名稱改為選擇下拉選單 */}
        {currentTab.key === 'improvements' ? (
          <TextField
            fullWidth
            margin="normal"
            size="small"
            label="名稱"
              value={formData.name || ''}
            InputProps={{
              readOnly: true,
            }}
            helperText="名稱會根據選擇的改善動作自動設定"
            sx={{
              '& .MuiInputBase-input': {
                color: 'text.secondary',
                cursor: 'not-allowed'
              }
            }}
          />
        ) : (
          // 其他標籤保持原來的文字輸入
          <TextField
            fullWidth
            margin="normal"
            size="small"
            label="名稱"
            value={formData.name || ''}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
        )}

        {/* 動作詳細需要技能類別選擇 */}
        {currentTab.key === 'movementDetails' && (
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel>技能類別</InputLabel>
            <Select
              value={formData.skill_category || 'position'}
              onChange={(e) => setFormData({ ...formData, skill_category: e.target.value })}
              required
            >
              <MenuItem value="position">Position</MenuItem>
              <MenuItem value="balancing">Balancing</MenuItem>
              <MenuItem value="rotation">Rotation</MenuItem>
              <MenuItem value="counter_rotation">Counter Rotation</MenuItem>
              <MenuItem value="inclination">Inclination</MenuItem>
              <MenuItem value="angulation">Angulation</MenuItem>
              <MenuItem value="reactive">Reactive</MenuItem>
              <MenuItem value="proactive">Proactive</MenuItem>
              <MenuItem value="timing">Timing</MenuItem>
              <MenuItem value="coordination">Coordination</MenuItem>
            </Select>
          </FormControl>
        )}

        {/* 動作詳細需要移動方向選擇 */}
        {currentTab.key === 'movementDetails' && (
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel>移動方向</InputLabel>
            <Select
              value={formData.move_direction || 'up_and_down'}
              onChange={(e) => setFormData({ ...formData, move_direction: e.target.value })}
              required
            >
              <MenuItem value="up_and_down">Up and Down</MenuItem>
              <MenuItem value="fore_and_after">Fore and After</MenuItem>
              <MenuItem value="edge_to_edge">Edge to Edge</MenuItem>
              <MenuItem value="rotation">Rotation</MenuItem>
            </Select>
          </FormControl>
        )}

        {/* 動作詳細需要難度等級選擇 */}
        {currentTab.key === 'movementDetails' && (
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel>難度等級</InputLabel>
            <Select
              value={formData.difficulty_level || 1}
              onChange={(e) => setFormData({ ...formData, difficulty_level: parseInt(e.target.value) })}
              required
            >
              <MenuItem value={1}>1級 - 簡單</MenuItem>
              <MenuItem value={2}>2級 - 容易</MenuItem>
              <MenuItem value={3}>3級 - 中等</MenuItem>
              <MenuItem value={4}>4級 - 困難</MenuItem>
              <MenuItem value={5}>5級 - 極難</MenuItem>
            </Select>
          </FormControl>
        )}

        {/* 問題類別 - 動作類別需要移動方向選擇 */}
        {currentTab.key === 'problemCategories' && selectedProblemCategoryType === 'movement' && (
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel>移動方向</InputLabel>
            <Select
              value={formData.move_direction || ''}
              onChange={(e) => setFormData({ ...formData, move_direction: e.target.value })}
            >
              <MenuItem value="up_and_down">Up and Down</MenuItem>
              <MenuItem value="fore_and_after">Fore and After</MenuItem>
              <MenuItem value="edge_to_edge">Edge to Edge</MenuItem>
              <MenuItem value="rotation">Rotation</MenuItem>
            </Select>
          </FormControl>
        )}

        {/* 問題類別需要技能類別選擇 */}
        {currentTab.key === 'problemCategories' && (
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel>技能類別</InputLabel>
            <Select
              value={formData.skill_category || ''}
              onChange={(e) => setFormData({ ...formData, skill_category: e.target.value })}
            >
              <MenuItem value="">請選擇技能類別</MenuItem>
              <MenuItem value="position">Position</MenuItem>
              <MenuItem value="balancing">Balancing</MenuItem>
              <MenuItem value="rotation">Rotation</MenuItem>
              <MenuItem value="counter_rotation">Counter Rotation</MenuItem>
              <MenuItem value="inclination">Inclination</MenuItem>
              <MenuItem value="angulation">Angulation</MenuItem>
              <MenuItem value="reactive">Reactive</MenuItem>
              <MenuItem value="proactive">Proactive</MenuItem>
              <MenuItem value="timing">Timing</MenuItem>
              <MenuItem value="coordination">Coordination</MenuItem>
            </Select>
          </FormControl>
        )}

        {/* 問題類別新增時的提示信息 */}
        {currentTab.key === 'problemCategories' && !editingItem && (
          <Alert severity="info" sx={{ mt: 2 }}>
            新增時將使用外層選擇的類別類型：{selectedProblemCategoryType === 'performance' ? '表現' : 
            selectedProblemCategoryType === 'phase' ? '階段' : '動作'}
          </Alert>
        )}


        {/* 改善類別需要技能類別選擇 */}
        {currentTab.key === 'improvementCategories' && (
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel>技能類別</InputLabel>
            <Select
              value={formData.skill_category || ''}
              onChange={(e) => setFormData({ ...formData, skill_category: e.target.value })}
            >
              <MenuItem value="">請選擇技能類別</MenuItem>
              <MenuItem value="position">Position</MenuItem>
              <MenuItem value="balancing">Balancing</MenuItem>
              <MenuItem value="rotation">Rotation</MenuItem>
              <MenuItem value="counter_rotation">Counter Rotation</MenuItem>
              <MenuItem value="inclination">Inclination</MenuItem>
              <MenuItem value="angulation">Angulation</MenuItem>
              <MenuItem value="reactive">Reactive</MenuItem>
              <MenuItem value="proactive">Proactive</MenuItem>
              <MenuItem value="timing">Timing</MenuItem>
              <MenuItem value="coordination">Coordination</MenuItem>
            </Select>
          </FormControl>
        )}

        {/* 改善類別新增時的提示信息 */}
        {currentTab.key === 'improvementCategories' && !editingItem && (
          <Alert severity="info" sx={{ mt: 2 }}>
            新增時將使用外層選擇的類別類型：{selectedImprovementCategoryType === 'exercise' ? '練習' : '動作'}
          </Alert>
        )}

        {/* 問題需要類別選擇 */}
        {currentTab.key === 'problems' && (
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel>類別</InputLabel>
            <Select
              value={formData.category || ''}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              required
            >
              {problemCategories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* 改善項目需要類別選擇 - 編輯時顯示為只讀 */}
        {currentTab.key === 'improvements' && (
          <>
            {editingItem ? (
              // 編輯模式：顯示為只讀的 TextField
              <TextField
                fullWidth
                margin="normal"
                size="small"
                label="類別"
                value={formData.category ? 
                  improvementCategories.find(cat => cat.id === formData.category)?.name || '未知類別' 
                  : ''}
                InputProps={{
                  readOnly: true,
                }}
                helperText="編輯模式下無法更改類別"
                sx={{
                  '& .MuiInputBase-input': {
                    color: 'text.secondary',
                    cursor: 'not-allowed'
                  }
                }}
              />
            ) : (
              // 新增模式：顯示為可選擇的 Select
              <FormControl fullWidth margin="normal" size="small">
                <InputLabel>類別</InputLabel>
                <Select
                  value={formData.category || ''}
                  onChange={async (e) => {
                    const categoryId = e.target.value;
                    const selectedCategory = improvementCategories.find(cat => cat.id === categoryId);
                    
                    // 自動設定名稱為選擇類別的技能類別
                    const skillCategory = selectedCategory?.skill_category || '';
                    
                    setFormData({ 
                      ...formData, 
                      category: categoryId,
                      name: skillCategory // 自動設定名稱為技能類別
                    });
                    
                    // 根據技能類別過濾動作詳細
                    if (skillCategory) {
                      try {
                        const movementDetails = await evaluationDatabaseApi.getAllMovementDetails();
                        const filteredDetails = movementDetails.filter(detail => 
                          detail.skill_category === skillCategory
                        );
                        setAvailableMovementDetails(filteredDetails);
                      } catch (err) {
                        console.error('Failed to load movement details:', err);
                        setAvailableMovementDetails([]);
                      }
                    } else {
                        setAvailableMovementDetails([]);
                    }
                  }}
                  required
                >
                  {/* 只顯示動作類型的改善類別，且還沒有對應改善項目的類別 */}
                  {improvementCategories
                    .filter(cat => cat.category_type === 'movement')
                    .filter(cat => {
                      const hasImprovement = improvements.some(imp => imp.category === cat.id);
                      return !hasImprovement;
                    })
                    .map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}
          </>
        )}

        {/* 新增模式下的提示信息 */}
        {currentTab.key === 'improvements' && !editingItem && (
          <Alert severity="info" sx={{ mt: 1 }}>
            只能為尚未有改善項目的類別新增項目。如需修改現有類別的改善項目，請先編輯該項目。
          </Alert>
        )}

        {/* 改善項目的動作詳細選擇 - 帶順序安排 */}
        {currentTab.key === 'improvements' && availableMovementDetails.length > 0 && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
              相關動作詳細（可拖拽調整順序）
            </Typography>
            
            {/* 已選擇的動作詳細列表 */}
            <Box sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: 1, p: 1, minHeight: 100 }}>
              {(formData.movement_detail_orders || []).map((item, index) => {
                const detail = availableMovementDetails.find(d => d.id === item.movement_detail_id);
                if (!detail) return null;
                
                return (
                  <Box
                    key={item.movement_detail_id}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData('text/plain', index.toString());
                      e.currentTarget.style.opacity = '0.5';
                    }}
                    onDragEnd={(e) => {
                      e.currentTarget.style.opacity = '1';
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const draggedIndex = parseInt(e.dataTransfer.getData('text/plain'));
                      const targetIndex = index;
                      
                      if (draggedIndex === targetIndex) return;
                      
                      const newOrders = [...(formData.movement_detail_orders || [])];
                      const draggedItem = newOrders[draggedIndex];
                      
                      // 移除被拖拽的項目
                      newOrders.splice(draggedIndex, 1);
                      // 插入到新位置
                      newOrders.splice(targetIndex, 0, draggedItem);
                      
                      // 更新 order 值
                      const updatedOrders = newOrders.map((item, idx) => ({
                        ...item,
                        order: idx
                      }));
                      
                      setFormData({
                        ...formData,
                        movement_detail_orders: updatedOrders
                      });
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 1,
                      mb: 1,
                      border: '1px solid',
                      borderColor: 'grey.200',
                      borderRadius: 1,
                      backgroundColor: 'grey.50',
                      cursor: 'move',
                      '&:hover': {
                        backgroundColor: 'grey.100'
                      }
                    }}
                  >
                    <Typography variant="body2" sx={{ mr: 2, minWidth: 20, textAlign: 'center' }}>
                      {index + 1}
                    </Typography>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight="bold">
                        {detail.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {detail.skill_category_display} - {detail.move_direction_display} - {detail.difficulty_level}級
                      </Typography>
                </Box>
                    <IconButton
                      size="small"
                      onClick={() => {
                        const newOrders = (formData.movement_detail_orders || []).filter(
                          (_, i) => i !== index
                        );
                        setFormData({ ...formData, movement_detail_orders: newOrders });
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                );
              })}
              
              {(!formData.movement_detail_orders || formData.movement_detail_orders.length === 0) && (
                <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                  尚未選擇動作詳細
                </Typography>
              )}
            </Box>
            
            {/* 可選擇的動作詳細列表 */}
            <FormControl fullWidth margin="normal" size="small">
              <InputLabel>添加動作詳細</InputLabel>
              <Select
                value=""
                onChange={(e) => {
                  const detailId = e.target.value;
                  if (!detailId) return;
                  
                  const detail = availableMovementDetails.find(d => d.id === detailId);
                  if (!detail) return;
                  
                  // 檢查是否已經選擇
                  const existingOrders = formData.movement_detail_orders || [];
                  if (existingOrders.some(item => item.movement_detail_id === detailId)) {
                    return;
                  }
                  
                  // 添加新的動作詳細，順序為當前數量
                  const newOrder = {
                    movement_detail_id: detailId,
                    order: existingOrders.length
                  };
                  
                  setFormData({
                    ...formData,
                    movement_detail_orders: [...existingOrders, newOrder]
                  });
                  
                  // 清空選擇
                  e.target.value = '';
                }}
              >
                {availableMovementDetails
                  .filter(detail => 
                    !(formData.movement_detail_orders || []).some(item => item.movement_detail_id === detail.id)
                  )
                  .map((detail) => (
                <MenuItem key={detail.id} value={detail.id}>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {detail.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {detail.skill_category_display} - {detail.move_direction_display} - {detail.difficulty_level}級
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          </Box>
        )}

        {/* 改善項目的動作詳細選擇 - 無可用選項時的提示 */}
        {currentTab.key === 'improvements' && availableMovementDetails.length === 0 && formData.name && (
          <Alert severity="info" sx={{ mt: 1 }}>
            選擇的技能類別「{formData.name}」目前沒有對應的動作詳細。請先添加動作詳細或選擇其他技能類別。
          </Alert>
        )}

        {/* 等級選擇 */}
        {currentTab.key === 'problems' && (
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel>嚴重程度</InputLabel>
            <Select
              value={formData.severity_level || 1}
              onChange={(e) => setFormData({ ...formData, severity_level: e.target.value })}
            >
              {[1, 2, 3, 4, 5].map((level) => (
                <MenuItem key={level} value={level}>
                  {level}級
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}


        {currentTab.key === 'improvements' && (
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel>效果等級</InputLabel>
            <Select
              value={formData.effectiveness_level || 1}
              onChange={(e) => setFormData({ ...formData, effectiveness_level: e.target.value })}
            >
              {[1, 2, 3, 4, 5].map((level) => (
                <MenuItem key={level} value={level}>
                  {level}級
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* 描述欄位 */}
        <TextField
          fullWidth
          margin="normal"
          size="small"
          label="描述"
          value={formData.description || ''}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          multiline
          rows={2}
        />
      </Box>
    );
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const currentTab = tabConfig[activeTab];
      const apiMethods = apiMethodMap[currentTab.key];
      
      // 準備要發送的數據
      let dataToSend = { ...formData };
      
      // 如果是改善項目，確保 movement_detail_ids 和 movement_detail_orders 正確發送
      if (currentTab.key === 'improvements') {
        dataToSend = {
          ...formData,
          movement_detail_orders: formData.movement_detail_orders || []
        };
      }
      
      const result = editingItem
        ? await evaluationDatabaseApi[apiMethods.update](editingItem.id, dataToSend)
        : await evaluationDatabaseApi[apiMethods.create](dataToSend);

      let updatedData = editingItem
        ? currentTab.data.map(item => item.id === editingItem.id ? result : item)
        : [...currentTab.data, result];
      
      // 根據不同的標籤應用相應的排序
      if (currentTab.key === 'movementDetails') {
        updatedData = sortMovementDetails(updatedData);
      } else if (currentTab.key === 'improvementCategories') {
        updatedData = sortImprovementCategories(updatedData);
      } else if (currentTab.key === 'problemCategories') {
        updatedData = sortProblemCategories(updatedData);
      } else if (currentTab.key === 'improvements') {
        updatedData = sortImprovements(updatedData);
      }
      
      currentTab.setData(updatedData);
      setSuccess(editingItem ? '更新成功' : '創建成功');
      handleCloseDialog();
    } catch (err) {
      setError('保存失敗: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (item) => {
    const currentTab = tabConfig[activeTab];
    
    // 檢查級聯刪除的影響
    let confirmMessage = '確定要刪除這個項目嗎？';
    let affectedItems = [];
    
    if (currentTab.key === 'improvements') {
      // 改善項目被刪除時，檢查是否有相關的動作詳細
      const relatedMovementDetails = movementDetails.filter(md => 
        item.improvement_movement_details && item.improvement_movement_details.some(related => related.movement_detail.id === md.id)
      );
      
      if (relatedMovementDetails.length > 0) {
        affectedItems.push(`相關的動作詳細：${relatedMovementDetails.map(md => md.name).join(', ')}`);
      }
    } else if (currentTab.key === 'movementDetails') {
      // 動作詳細被刪除時，檢查是否有改善項目使用它
      const relatedImprovements = improvements.filter(imp => 
        imp.improvement_movement_details && imp.improvement_movement_details.some(related => related.movement_detail.id === item.id)
      );
      
      if (relatedImprovements.length > 0) {
        affectedItems.push(`使用此動作詳細的改善項目：${relatedImprovements.map(imp => imp.name).join(', ')}`);
      }
    }
    
    if (affectedItems.length > 0) {
      confirmMessage += `\n\n注意：此操作會影響以下項目：\n${affectedItems.join('\n')}\n\n刪除後，相關的關聯關係將會被移除。`;
    }
    
    if (window.confirm(confirmMessage)) {
      try {
        setLoading(true);
        await evaluationDatabaseApi[apiMethodMap[currentTab.key].delete](item.id);
        
        // 更新當前標籤的數據
        let updatedData = currentTab.data.filter(i => i.id !== item.id);
        
        // 根據不同的標籤應用相應的排序
        if (currentTab.key === 'movementDetails') {
          updatedData = sortMovementDetails(updatedData);
        } else if (currentTab.key === 'improvementCategories') {
          updatedData = sortImprovementCategories(updatedData);
        } else if (currentTab.key === 'problemCategories') {
          updatedData = sortProblemCategories(updatedData);
        }
        
        currentTab.setData(updatedData);
        
            // 如果是動作詳細被刪除，需要更新所有使用它的改善項目
            if (currentTab.key === 'movementDetails') {
              const updatedImprovements = improvements.map(imp => ({
                ...imp,
                improvement_movement_details: imp.improvement_movement_details.filter(md => md.movement_detail.id !== item.id)
              }));
              setImprovements(updatedImprovements);
            }
        
        // 如果是改善項目被刪除，動作詳細的關聯會自動處理（因為是 ManyToMany 關係）
        
        setSuccess('刪除成功');
      } catch (err) {
        setError('刪除失敗: ' + err.message);
      } finally {
        setLoading(false);
      }
    }
  };

  // 新增：過濾問題類別的函數
  const getFilteredProblemCategories = () => {
    const filtered = problemCategories.filter(cat => cat.category_type === selectedProblemCategoryType);
    return sortProblemCategories(filtered);
  };

  // 新增：問題類別排序函數
  const sortProblemCategories = (categories) => {
    // 移動方向排序順序
    const moveDirectionOrder = [
      'up_and_down',
      'fore_and_after', 
      'edge_to_edge',
      'rotation'
    ];
    
    return categories.sort((a, b) => {
      // 首先按照技能類別排序
      const aSkillIndex = a.skill_category ? skillCategoryOrder.indexOf(a.skill_category) : -1;
      const bSkillIndex = b.skill_category ? skillCategoryOrder.indexOf(b.skill_category) : -1;
      
      // 如果 skill_category 不在預定義順序中，放在最後
      if (aSkillIndex === -1 && bSkillIndex === -1) {
        // 如果都沒有技能類別，按照移動方向排序
        const aDirectionIndex = moveDirectionOrder.indexOf(a.move_direction);
        const bDirectionIndex = moveDirectionOrder.indexOf(b.move_direction);
        
        if (aDirectionIndex !== -1 && bDirectionIndex !== -1) {
          return aDirectionIndex - bDirectionIndex;
        }
        
        if (aDirectionIndex !== -1 && bDirectionIndex === -1) {
          return -1;
        }
        if (aDirectionIndex === -1 && bDirectionIndex !== -1) {
          return 1;
        }
        
        // 最後按名稱排序
        return a.name.localeCompare(b.name);
      }
      if (aSkillIndex === -1) return 1;
      if (bSkillIndex === -1) return -1;
      
      if (aSkillIndex !== bSkillIndex) {
        return aSkillIndex - bSkillIndex;
      }
      
      // 技能類別相同時，按照移動方向排序
      const aDirectionIndex = moveDirectionOrder.indexOf(a.move_direction);
      const bDirectionIndex = moveDirectionOrder.indexOf(b.move_direction);
      
      if (aDirectionIndex !== -1 && bDirectionIndex !== -1) {
        return aDirectionIndex - bDirectionIndex;
      }
      
      if (aDirectionIndex !== -1 && bDirectionIndex === -1) {
        return -1;
      }
      if (aDirectionIndex === -1 && bDirectionIndex !== -1) {
        return 1;
      }
      
      // 最後按名稱排序
      return a.name.localeCompare(b.name);
    });
  };

  // 新增：過濾改善類別的函數
  const getFilteredImprovementCategories = () => {
    const filtered = improvementCategories.filter(cat => cat.category_type === selectedImprovementCategoryType);
    return sortImprovementCategories(filtered);
  };

  // 新增：過濾動作詳細的函數
  const getFilteredMovementDetails = () => {
    if (selectedSkillCategory === 'all') {
      return movementDetails;
    }
    return movementDetails.filter(detail => detail.skill_category === selectedSkillCategory);
  };

  // 技能類別篩選按鈕組件
  const renderSkillCategoryFilter = () => {
    if (activeTab !== 6) return null; // 只在動作詳細標籤顯示
    
    const skillCategories = [
      { value: 'all', label: '全部' },
      { value: 'position', label: 'Position' },
      { value: 'balancing', label: 'Balancing' },
      { value: 'rotation', label: 'Rotation' },
      { value: 'counter_rotation', label: 'Counter Rotation' },
      { value: 'inclination', label: 'Inclination' },
      { value: 'angulation', label: 'Angulation' },
      { value: 'reactive', label: 'Reactive' },
      { value: 'proactive', label: 'Proactive' },
      { value: 'timing', label: 'Timing' },
      { value: 'coordination', label: 'Coordination' }
    ];
    
    return (
      <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="h6" gutterBottom>
          技能類別篩選
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {skillCategories.map((category) => (
            <Chip
              key={category.value}
              label={category.label}
              onClick={() => setSelectedSkillCategory(category.value)}
              color={selectedSkillCategory === category.value ? 'primary' : 'default'}
              variant={selectedSkillCategory === category.value ? 'filled' : 'outlined'}
              sx={{ cursor: 'pointer' }}
            />
          ))}
        </Box>
      </Box>
    );
  };

  // 改善項目統計組件
  const renderImprovementStats = () => {
    if (activeTab !== 7) return null; // 只在改善項目標籤顯示
    
    const skillCategories = [
      { value: 'all', label: '全部' },
      { value: 'position', label: 'Position' },
      { value: 'balancing', label: 'Balancing' },
      { value: 'rotation', label: 'Rotation' },
      { value: 'counter_rotation', label: 'Counter Rotation' },
      { value: 'inclination', label: 'Inclination' },
      { value: 'angulation', label: 'Angulation' },
      { value: 'reactive', label: 'Reactive' },
      { value: 'proactive', label: 'Proactive' },
      { value: 'timing', label: 'Timing' },
      { value: 'coordination', label: 'Coordination' }
    ];
    
    // 獲取改善類別數據
    const movementCategories = improvementCategories.filter(cat => cat.category_type === 'movement');
    
    // 根據選擇的類別篩選數據
    let filteredCategories = movementCategories;
    if (selectedImprovementStatsCategory !== 'all') {
      filteredCategories = movementCategories.filter(cat => cat.skill_category === selectedImprovementStatsCategory);
    }
    
    // 統計關聯狀態
    const totalCategories = filteredCategories.length;
    const associatedCategories = filteredCategories.filter(cat => 
      improvements.some(imp => imp.category === cat.id)
    );
    const unassociatedCategories = filteredCategories.filter(cat => 
      !improvements.some(imp => imp.category === cat.id)
    );
    
    return (
      <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="h6" gutterBottom>
          改善項目統計
        </Typography>
        
        {/* 分類選擇按鈕 */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            選擇統計範圍：
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {skillCategories.map((category) => (
              <Chip
                key={category.value}
                label={category.label}
                onClick={() => setSelectedImprovementStatsCategory(category.value)}
                color={selectedImprovementStatsCategory === category.value ? 'primary' : 'default'}
                variant={selectedImprovementStatsCategory === category.value ? 'filled' : 'outlined'}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
        </Box>
        
        {/* 統計數據 */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Chip 
            label={`總類別數: ${totalCategories}`} 
            color="default" 
            variant="outlined"
          />
          <Chip 
            label={`已建立關聯: ${associatedCategories.length}`} 
            color="success" 
            variant="filled"
          />
          <Chip 
            label={`尚未建立關聯: ${unassociatedCategories.length}`} 
            color="warning" 
            variant="outlined"
          />
          <Chip 
            label={`完成率: ${totalCategories > 0 ? Math.round((associatedCategories.length / totalCategories) * 100) : 0}%`} 
            color={associatedCategories.length / totalCategories > 0.8 ? 'success' : 'warning'} 
            variant="outlined"
          />
        </Box>
        
        {/* 詳細列表 */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            詳細狀態：
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {filteredCategories.map(cat => {
              const hasImprovement = improvements.some(imp => imp.category === cat.id);
              return (
                <Chip
                  key={cat.id}
                  label={`${cat.name}: ${hasImprovement ? '已有' : '無'}`}
                  size="small"
                  color={hasImprovement ? 'success' : 'default'}
                  variant={hasImprovement ? 'filled' : 'outlined'}
                />
              );
            })}
          </Box>
        </Box>
      </Box>
    );
  };

  // 統計面板
  const renderStatsPanel = () => {
    if (activeTab !== 6) return null; // 只在動作詳細標籤顯示
    
    const filteredMovementDetails = getFilteredMovementDetails();
    const totalMovementDetails = filteredMovementDetails.length;
    const associatedCount = filteredMovementDetails.filter(item => item.is_associated).length;
    const unassociatedCount = totalMovementDetails - associatedCount;
    
    return (
      <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="h6" gutterBottom>
          動作詳細關聯統計
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Chip 
            label={`總數: ${totalMovementDetails}`} 
            color="default" 
            variant="outlined"
          />
          <Chip 
            label={`已關聯: ${associatedCount}`} 
            color="success" 
            variant="filled"
          />
          <Chip 
            label={`未關聯: ${unassociatedCount}`} 
            color="warning" 
            variant="outlined"
          />
          <Chip 
            label={`關聯率: ${totalMovementDetails > 0 ? Math.round((associatedCount / totalMovementDetails) * 100) : 0}%`} 
            color={associatedCount / totalMovementDetails > 0.8 ? 'success' : 'warning'} 
            variant="outlined"
          />
        </Box>
      </Box>
    );
  };

  // 簡化的手機版卡片顯示
  const renderMobileCards = (data) => {
    // 根據當前標籤使用過濾後的數據
    let displayData = data;
    if (activeTab === 3) { // 問題類別
      displayData = getFilteredProblemCategories();
    } else if (activeTab === 5) { // 改善類別
      displayData = getFilteredImprovementCategories();
    } else if (activeTab === 6) { // 動作詳細
      displayData = getFilteredMovementDetails();
    }
    
    const paginatedData = displayData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    
    return (
      <Box>
        {renderSkillCategoryFilter()}
        {renderStatsPanel()}
        {paginatedData.map((item) => (
          <Card key={item.id} sx={{ mb: 1.5, p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box flex={1}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {item.name}
                </Typography>
                {item.description && (
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                    {item.description.length > 50 
                      ? `${item.description.substring(0, 50)}...` 
                      : item.description
                    }
                  </Typography>
                )}
                
                {/* 顯示技能類別 */}
                {item.skill_category && (
                  <Chip 
                    label={item.skill_category_display || item.skill_category}
                    size="small" 
                    color="primary"
                    sx={{ mt: 1 }}
                  />
                )}
                
                {/* 顯示移動方向 */}
                {item.move_direction && (
                  <Chip 
                    label={item.move_direction_display || item.move_direction}
                    size="small" 
                    color="secondary"
                    sx={{ mt: 1, ml: 0.5 }}
                  />
                )}
                
                {/* 顯示關聯的改善類別（動作詳細） */}
                {item.category_name && (
                  <Chip 
                    label={`類別: ${item.category_name}`}
                    size="small" 
                    color="secondary"
                    sx={{ mt: 1, ml: 0.5 }}
                  />
                )}
                {/* 顯示直接關聯的動作詳細（改善項目） */}
                {item.detail_name && (
                  <Chip 
                    label={`直接關聯: ${item.detail_name}`}
                    size="small" 
                    color="info"
                    sx={{ mt: 1, ml: 0.5 }}
                  />
                )}
                {item.severity_level && (
                  <Chip 
                    label={`嚴重程度 ${item.severity_level}級`} 
                    size="small" 
                    color={item.severity_level >= 4 ? 'error' : item.severity_level >= 3 ? 'warning' : 'default'}
                    sx={{ mt: 1, ml: 0.5 }}
                  />
                )}
                {item.difficulty_level && (
                  <Chip 
                    label={`難度 ${item.difficulty_level}級`} 
                    size="small" 
                    color={item.difficulty_level >= 4 ? 'error' : item.difficulty_level >= 3 ? 'warning' : 'default'}
                    sx={{ mt: 1, ml: 0.5 }}
                  />
                )}
                {item.effectiveness_level && (
                  <Chip 
                    label={`效果 ${item.effectiveness_level}級`} 
                    size="small" 
                    color={item.effectiveness_level >= 4 ? 'success' : item.effectiveness_level >= 3 ? 'warning' : 'default'}
                    sx={{ mt: 1, ml: 0.5 }}
                  />
                )}
              </Box>
              <Box>
                <IconButton size="small" onClick={() => handleOpenDialog(item)}>
                  <EditIcon />
                </IconButton>
                <IconButton size="small" onClick={() => handleDelete(item)} color="error">
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
          </Card>
        ))}
        
        <Box display="flex" justifyContent="center" mt={2}>
          <TablePagination
            component="div"
            count={displayData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="每頁"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
          />
        </Box>
      </Box>
    );
  };

  // 動態表格欄位配置
  const getTableColumns = (tabIndex) => {
    const columnConfigs = {
      0: { // 環境
        columns: [
          { key: 'name', label: '名稱', width: 'auto' },
          { key: 'description', label: '描述', width: 'auto' }
        ]
      },
      1: { // 裝備
        columns: [
          { key: 'name', label: '名稱', width: 'auto' },
          { key: 'description', label: '描述', width: 'auto' }
        ]
      },
      2: { // 姿勢
        columns: [
          { key: 'name', label: '名稱', width: 'auto' },
          { key: 'description', label: '描述', width: 'auto' }
        ]
      },
      3: { // 問題類別
        columns: [
          { key: 'name', label: '名稱', width: 'auto' },
          { key: 'category_type', label: '類別類型', width: '120px' },
          { key: 'move_direction', label: '移動方向', width: '120px' },
          { key: 'skill_category', label: '技能類別', width: '120px' },
          { key: 'description', label: '描述', width: 'auto' }
        ]
      },
      4: { // 問題
        columns: [
          { key: 'name', label: '名稱', width: 'auto' },
          { key: 'category', label: '類別', width: '120px' },
          { key: 'description', label: '描述', width: 'auto' }
        ]
      },
      5: { // 改善類別
        columns: [
          { key: 'name', label: '名稱', width: 'auto' },
          { key: 'category_type', label: '類別類型', width: '120px' },
          { key: 'skill_category', label: '技能類別', width: '120px' },
          { key: 'description', label: '描述', width: 'auto' }
        ]
      },
      6: { // 動作詳細
        columns: [
          { key: 'name', label: '名稱', width: 'auto' },
          { key: 'description', label: '描述', width: 'auto' },
          { key: 'skill_category', label: '技巧分類', width: '120px' },
          { key: 'move_direction', label: '移動方向', width: '120px' },
          { key: 'difficulty_level', label: '難度', width: '80px' },
          { key: 'associated_improvements', label: '關聯數', width: '80px' }
        ]
      },
      7: { // 改善項目
        columns: [
          { key: 'name', label: '名稱', width: 'auto' },
          { key: 'category_name', label: '改善動作', width: 'auto' },
          { key: 'description', label: '描述', width: 'auto' },
          { key: 'effectiveness_level', label: '難度', width: '80px' },
          { key: 'improvement_movement_details', label: '相關動作描述', width: 'auto' }
        ]
      }
    };
    
    return columnConfigs[tabIndex] || columnConfigs[0];
  };

  // 動態表格欄位渲染
  const renderTableCell = (item, column) => {
    const value = item[column.key];
    
    switch (column.key) {
      case 'category_type':
        return (
          <Chip 
            label={value} 
            size="small" 
            color="primary"
            variant="outlined"
          />
        );
      case 'description':
        return (
          <Typography 
            variant="body2" 
            color="textSecondary"
            sx={{ 
              maxWidth: 200,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {value || '-'}
          </Typography>
        );
      case 'name':
        // 如果是動作詳細且未關聯，添加視覺化指示
        const isUnassociated = item.is_associated === false;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography 
              variant="subtitle2" 
              fontWeight="bold"
              sx={{ 
                color: isUnassociated ? 'warning.main' : 'inherit',
                opacity: isUnassociated ? 0.7 : 1
              }}
            >
              {value}
            </Typography>
            {isUnassociated && (
              <Chip 
                label="未關聯" 
                size="small" 
                color="warning" 
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
            )}
          </Box>
        );
      case 'category_name':
        return (
          <Typography variant="body2" fontWeight="medium">
            {value || '-'}
          </Typography>
        );
      case 'category':
        return (
          <Chip 
            label={value} 
            size="small" 
            color="secondary"
            variant="outlined"
          />
        );
      case 'skill_category':
        // 技能類別映射
        const skillCategoryMap = {
          'position': 'Position',
          'balancing': 'Balancing',
          'rotation': 'Rotation',
          'counter_rotation': 'Counter Rotation',
          'inclination': 'Inclination',
          'angulation': 'Angulation',
          'reactive': 'Reactive',
          'proactive': 'Proactive',
          'timing': 'Timing',
          'coordination': 'Coordination'
        };
        
        const displayValue = skillCategoryMap[value] || value || '-';
        
        return (
          <Chip 
            label={displayValue} 
            size="small" 
            color="error"
            variant="outlined"
          />
        );
      case 'move_direction':
        // 移動方向映射
        const moveDirectionMap = {
          'up_and_down': 'Up and Down',
          'fore_and_after': 'Fore and After',
          'edge_to_edge': 'Edge to Edge',
          'rotation': 'Rotation'
        };
        
        const moveDirectionValue = moveDirectionMap[value] || value || '-';
        
        return (
          <Chip 
            label={moveDirectionValue} 
            size="small" 
            color="warning"
            variant="outlined"
          />
        );
      case 'associated_improvements':
        // 關聯數顯示
        const improvements = value || [];
        const count = improvements.length;
        return (
          <Typography 
            variant="body2" 
            fontWeight="bold"
            color={count > 0 ? 'success.main' : 'textSecondary'}
            sx={{ textAlign: 'center' }}
          >
            {count}
          </Typography>
        );
      case 'difficulty_level':
        // 難度等級顯示
        const difficultyValue = value || 1;
        const difficultyColor = {
          1: 'success',    // 綠色 - 簡單
          2: 'info',       // 藍色 - 容易
          3: 'warning',    // 橘色 - 中等
          4: 'error',      // 紅色 - 困難
          5: 'secondary'   // 紫色 - 極難
        }[difficultyValue] || 'default';
        
        return (
          <Chip 
            label={`${difficultyValue}級`} 
            size="small" 
            color={difficultyColor}
            variant="filled"
          />
        );
      case 'effectiveness_level':
        // 效果等級顯示（改善項目的難度）
        const effectivenessValue = value || 1;
        const effectivenessColor = {
          1: 'success',    // 綠色 - 簡單
          2: 'info',       // 藍色 - 容易
          3: 'warning',    // 橘色 - 中等
          4: 'error',      // 紅色 - 困難
          5: 'secondary'   // 紫色 - 極難
        }[effectivenessValue] || 'default';
        
        return (
          <Chip 
            label={`${effectivenessValue}級`} 
            size="small" 
            color={effectivenessColor}
            variant="filled"
          />
        );
      case 'improvement_movement_details':
        // 處理相關動作描述，使用新的關聯表結構，按 order 排序
        let detailsArray = value;
        if (!Array.isArray(detailsArray)) {
          detailsArray = [];
        }
        
        // 按照 order 字段排序（創建新數組避免修改原數組）
        const sortedDetails = [...detailsArray].sort((a, b) => {
          const orderA = a.order || 0;
          const orderB = b.order || 0;
          return orderA - orderB;
        });
        
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 200 }}>
            {sortedDetails.length > 0 ? (
              sortedDetails.map((detail, index) => {
                // 處理新的關聯表結構：improvement_movement_details
                const detailText = detail.movement_detail 
                  ? detail.movement_detail.name 
                  : (detail.name || detail);
                
                return (
                  <Chip 
                    key={index}
                    label={detailText} 
                    size="small" 
                    color="success"
                    variant="outlined"
                  />
                );
              })
            ) : (
              <Typography variant="body2" color="textSecondary">
                -
              </Typography>
            )}
          </Box>
        );
      default:
        return (
          <Typography variant="body2">
            {value || '-'}
          </Typography>
        );
    }
  };

  // 動態桌面版表格顯示
  const renderDesktopTable = (data) => {
    // 根據當前標籤使用過濾後的數據
    let displayData = data;
    if (activeTab === 3) { // 問題類別
      displayData = getFilteredProblemCategories();
    } else if (activeTab === 5) { // 改善類別
      displayData = getFilteredImprovementCategories();
    } else if (activeTab === 6) { // 動作詳細
      displayData = getFilteredMovementDetails();
    }
    
    const paginatedData = displayData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    const columns = getTableColumns(activeTab);
    
    return (
      <Box>
        {renderSkillCategoryFilter()}
        {renderStatsPanel()}
      <TableContainer component={Paper} elevation={0}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.columns.map((column) => (
                <TableCell 
                  key={column.key}
                  sx={{ width: column.width }}
                >
                  {column.label}
                </TableCell>
              ))}
              <TableCell width="120">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((item) => (
              <TableRow key={item.id} hover>
                {columns.columns.map((column) => (
                  <TableCell key={column.key}>
                    {renderTableCell(item, column)}
                </TableCell>
                ))}
                <TableCell>
                  <IconButton size="small" onClick={() => handleOpenDialog(item)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(item)} color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={displayData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="每頁"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
        />
      </TableContainer>
      </Box>
    );
  };

  const renderForm = () => {
    const currentTab = tabConfig[activeTab];
    
    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">{currentTab.label}管理</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            size="small"
            // 如果是改善項目且沒有可用的類別，禁用新增按鈕
            disabled={currentTab.key === 'improvements' && 
              improvementCategories
                .filter(cat => cat.category_type === 'movement')
                .every(cat => improvements.some(imp => imp.category === cat.id))}
          >
            新增
          </Button>
        </Box>
        
        {/* 改善項目的統計信息 */}
        {renderImprovementStats()}
        
        {/* 問題類別專用的類別選擇器 */}
        {currentTab.key === 'problemCategories' && (
          <Box sx={{ mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>顯示類別</InputLabel>
              <Select
                value={selectedProblemCategoryType}
                onChange={(e) => {
                  setSelectedProblemCategoryType(e.target.value);
                  setPage(0); // 重置分頁
                }}
              >
                <MenuItem value="performance">表現</MenuItem>
                <MenuItem value="phase">階段</MenuItem>
                <MenuItem value="movement">動作</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}
        
        {/* 改善類別專用的類別選擇器 */}
        {currentTab.key === 'improvementCategories' && (
          <Box sx={{ mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>顯示類別</InputLabel>
              <Select
                value={selectedImprovementCategoryType}
                onChange={(e) => {
                  setSelectedImprovementCategoryType(e.target.value);
                  setPage(0); // 重置分頁
                }}
              >
                <MenuItem value="exercise">練習</MenuItem>
                <MenuItem value="movement">動作</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}
        
        {isMobile ? renderMobileCards(currentTab.data) : renderDesktopTable(currentTab.data)}
      </Box>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ px: isMobile ? 1 : 2 }}>
      <Fade in={true}>
        <Box>
          <Typography variant={isMobile ? "h5" : "h4"} gutterBottom>
            評估資料庫管理
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}

          <Card elevation={1}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs 
                value={activeTab} 
                onChange={handleTabChange}
                variant={isMobile ? "scrollable" : "standard"}
                scrollButtons={isMobile ? "auto" : false}
                allowScrollButtonsMobile
              >
                {tabConfig.map((tab, index) => (
                  <Tab key={tab.key} label={tab.label} />
                ))}
              </Tabs>
            </Box>
            <CardContent sx={{ p: isMobile ? 1 : 2 }}>
              {loading ? (
                <Box display="flex" justifyContent="center" p={4}>
                  <CircularProgress />
                </Box>
              ) : (
                renderForm()
              )}
            </CardContent>
          </Card>

          {/* 簡化的編輯對話框 */}
          <Dialog 
            open={openDialog} 
            onClose={handleCloseDialog} 
            maxWidth="sm" 
            fullWidth
            fullScreen={isMobile}
          >
            <DialogTitle>
              {editingItem ? '編輯' : '新增'}{tabConfig[activeTab]?.label}
            </DialogTitle>
            <DialogContent sx={{ p: 2 }}>
              {renderFormFields()}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={handleCloseDialog} startIcon={<CancelIcon />}>
                取消
              </Button>
              <Button onClick={handleSave} variant="contained" startIcon={<SaveIcon />}>
                保存
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Fade>
    </Container>
  );
};

export default EvaluationDatabaseManage;
